import { useState, useEffect, useRef } from 'react';
import { Layout, api } from './_barrel.js';


        // =======================================================================
        // Layout / chrome
        // =======================================================================
        function NavSearch({ token, setView, setSelectedSubject, setSelectedBusiness }) {
            const [query, setQuery] = useState('');
            const [results, setResults] = useState(null);
            const [open, setOpen] = useState(false);
            const [loading, setLoading] = useState(false);
            const debounceRef = useRef(null);
            const wrapRef = useRef(null);

            // Close dropdown when clicking outside
            useEffect(() => {
                const onClick = (e) => {
                    if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                        setOpen(false);
                    }
                };
                document.addEventListener('mousedown', onClick);
                return () => document.removeEventListener('mousedown', onClick);
            }, []);

            useEffect(() => {
                if (!query.trim()) {
                    setResults(null);
                    return;
                }
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(async () => {
                    setLoading(true);
                    try {
                        const r = await api(`/api/v1/search?q=${encodeURIComponent(query.trim())}`, {}, token);
                        setResults(r);
                        setOpen(true);
                    } catch (e) {
                        setResults({ subjects: [], businesses: [], total: 0 });
                    } finally {
                        setLoading(false);
                    }
                }, 250);
                return () => debounceRef.current && clearTimeout(debounceRef.current);
            }, [query, token]);

            const pickSubject = (s) => {
                setSelectedSubject(s.id);
                setView('subject-detail');
                setQuery('');
                setResults(null);
                setOpen(false);
            };
            const pickBusiness = (b) => {
                setSelectedBusiness(b.id);
                setView('business-detail');
                setQuery('');
                setResults(null);
                setOpen(false);
            };

            return (
                <div ref={wrapRef} style={{position: 'relative', minWidth: '260px'}}>
                    <input
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onFocus={() => results && setOpen(true)}
                        placeholder="Search subjects, businesses…"
                        style={{
                            width: '100%',
                            fontSize: '13px',
                            padding: '8px 12px',
                            background: 'var(--zone-2)',
                            border: '1px solid rgba(201,169,110,0.2)',
                            color: 'var(--text)',
                            borderRadius: '3px',
                            fontFamily: 'inherit',
                        }}
                    />
                    {open && results && (
                        <div style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            right: 0,
                            left: 0,
                            background: 'var(--panel)',
                            border: '1px solid var(--gold)',
                            borderRadius: '3px',
                            maxHeight: '420px',
                            overflowY: 'auto',
                            zIndex: 50,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                        }}>
                            {results.total === 0 ? (
                                <div className="px-4 py-3 text-xs font-mono text-muted">
                                    No matches for "{results.query}"
                                </div>
                            ) : (
                                <>
                                    {results.subjects.length > 0 && (
                                        <div>
                                            <div className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted border-b border-veil">
                                                Subjects ({results.subjects.length})
                                            </div>
                                            {results.subjects.map(s => (
                                                <div key={s.id}
                                                     className="px-4 py-2 cursor-pointer hover:bg-[rgba(201,169,110,0.08)]"
                                                     onClick={() => pickSubject(s)}>
                                                    <div className="text-sm">
                                                        {s.display_name || s.full_name}
                                                        {s.is_archived && <span className="ml-2 text-xs" style={{color:'var(--text-dim)'}}>· archived</span>}
                                                    </div>
                                                    <div className="text-xs text-muted font-mono">
                                                        {s.subject_type_code}
                                                        {s.matched_via === 'alias' && ' · matched via alias'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {results.businesses.length > 0 && (
                                        <div>
                                            <div className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-muted border-b border-veil">
                                                Businesses ({results.businesses.length})
                                            </div>
                                            {results.businesses.map(b => (
                                                <div key={b.id}
                                                     className="px-4 py-2 cursor-pointer hover:bg-[rgba(201,169,110,0.08)]"
                                                     onClick={() => pickBusiness(b)}>
                                                    <div className="text-sm">
                                                        {b.display_name || b.legal_name}
                                                        {b.is_archived && <span className="ml-2 text-xs" style={{color:'var(--text-dim)'}}>· archived</span>}
                                                    </div>
                                                    <div className="text-xs text-muted font-mono">
                                                        {b.company_number && `${b.company_number} · `}{b.jurisdiction || '—'}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            );
        }

export { NavSearch };
